import {
  CalendarDays,
  ListTodo,
  NotebookPen,
  Settings,
  Users,
} from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import { SebasLogo } from "@/components/brand/SebasLogo";
import { SebasMark } from "@/components/brand/SebasMark";
import { MobileTabBar } from "@/components/layout/MobileTabBar";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { ProfileButton } from "@/components/layout/ProfileButton";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { cn } from "@/lib/cn";
import { useNavEntrance } from "@/lib/useNavEntrance";

const NAV = [
  { to: "/notes", label: "Notes", icon: NotebookPen },
  { to: "/tasks", label: "Tasks", icon: ListTodo },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/teams", label: "Teams", icon: Users },
  { to: "/settings", label: "Settings", icon: Settings },
] as const;

/**
 * Sidebar on desktop, bottom tab bar on mobile — one nav definition drives both,
 * so a route added here shows up in every form factor.
 */
export function AppShell() {
  const sidebarNav = useNavEntrance<HTMLElement>({ axis: "x", distance: 10 });

  return (
    <div className="min-h-dvh bg-surface text-ink">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-20 hidden w-60 flex-col border-r border-line bg-surface px-4 py-6",
          "lg:flex",
        )}
      >
        <div className="px-2">
          <div className="flex items-center justify-between">
            <SebasLogo />
            <div className="flex items-center gap-1">
              <NotificationBell />
              <ThemeToggle />
            </div>
          </div>
          {/* Sits under the wordmark: mark width (1.75rem) + lock-up gap (0.5rem). */}
          <span className="mt-1 block pl-9 text-xs text-ink-faint">v0.1</span>
        </div>

        <nav ref={sidebarNav} className="mt-4 flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-nav-item
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-pill px-4 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-accent text-accent-ink"
                    : "text-ink-muted hover:bg-sunken hover:text-ink",
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto border-t border-line pt-3">
          <ProfileButton />
        </div>
      </aside>

      {/* Mobile top bar — horizontal padding matches the page content below it;
          the notch inset stacks on top of the base padding rather than replacing it. */}
      <header
        className={cn(
          "sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-line",
          "bg-surface/85 px-5 pb-3.5 pt-[calc(env(safe-area-inset-top,0px)+0.875rem)]",
          "backdrop-blur-md lg:hidden",
        )}
      >
        <SebasMark className="h-7 w-7" title="Sebas" />
        <div className="flex items-center gap-2.5">
          <NotificationBell />
          <ThemeToggle />
          <ProfileButton variant="compact" />
        </div>
      </header>

      <main className="lg:pl-60">
        <div className="mx-auto w-full max-w-5xl px-5 pb-28 pt-6 lg:px-10 lg:pb-16 lg:pt-10">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tabs */}
      <MobileTabBar nav={NAV} />
    </div>
  );
}
